'use client'

/**
 * Draw-or-type signature pad. Emits a PNG data URL (or '' when cleared).
 * Used by FormRender's signature field (storefront forms) and the dealer's
 * bill of sale (portal). Colors read the storefront theme tokens with
 * neutral fallbacks, so it also renders correctly in the portal, which does
 * not define them.
 */
import { useRef, useState } from 'react'
import { SignaturePlaceholder } from './SignaturePlaceholder'

// ─── Signature pad ─────────────────────────────────────────────────────────
// Two signing modes, the common e-sign pattern:
//   • Draw — finger/mouse strokes on the canvas (uncontrolled; exported on
//     each pointer-up).
//   • Type — the signer types their name and it's rendered onto the SAME
//     canvas in a cursive font.
// Either way the emitted value is a PNG data URL (canvas.toDataURL), so the
// whole backend contract — schema, validator, storage, the admin image
// viewer, triage redaction — is identical for both modes and needs no change.
// Clearing / switching modes wipes the canvas and emits '' so the submitted
// signature always matches what's currently shown.

// System cursive stack — covers macOS / iOS (Snell Roundhand, Apple Chancery),
// Windows (Brush Script MT, Segoe Script), then the generic `cursive`. All are
// OS-resident, so the canvas can render them synchronously (no web-font load).
const SIGNATURE_CURSIVE = "'Snell Roundhand','Apple Chancery','Brush Script MT','Segoe Script',cursive"

export function SignaturePad({
  onChange,
  invalidProps,
}: {
  onChange: (next: string) => void
  invalidProps?: { 'aria-invalid'?: boolean; 'aria-describedby'?: string }
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const dirty = useRef(false)
  const [mode, setMode] = useState<'draw' | 'type'>('draw')
  const [typedName, setTypedName] = useState('')
  // Mirrors dirty.current as state so the decorative placeholder can fade out
  // on the first stroke (refs don't trigger a re-render).
  const [hasInk, setHasInk] = useState(false)

  function clearCanvas() {
    const c = canvasRef.current
    if (c) c.getContext('2d')!.clearRect(0, 0, c.width, c.height)
    dirty.current = false
    setHasInk(false)
  }

  // ── Draw mode ──
  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!
    const r = c.getBoundingClientRect()
    // Canvas backing store is fixed-size; scale client coords into it so the
    // stroke lands under the pointer even when CSS stretches the element.
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    }
  }

  function handleDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (mode !== 'draw') return
    drawing.current = true
    canvasRef.current!.setPointerCapture(e.pointerId)
    const ctx = canvasRef.current!.getContext('2d')!
    const p = pos(e)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
  }

  function handleMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (mode !== 'draw' || !drawing.current) return
    const ctx = canvasRef.current!.getContext('2d')!
    const p = pos(e)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#1f2937'
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    if (!dirty.current) {
      dirty.current = true
      setHasInk(true)
    }
  }

  function handleEnd() {
    if (mode !== 'draw' || !drawing.current) return
    drawing.current = false
    if (dirty.current) onChange(canvasRef.current!.toDataURL('image/png'))
  }

  // ── Type mode ──
  // Renders the typed name centered in cursive, shrinking the font until it
  // fits the canvas width, then exports the PNG. Empty input → emits ''.
  function renderTyped(raw: string) {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, c.width, c.height)
    const name = raw.trim()
    if (!name) {
      onChange('')
      return
    }
    ctx.fillStyle = '#1f2937'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    let size = 80
    do {
      ctx.font = `${size}px ${SIGNATURE_CURSIVE}`
      size -= 4
    } while (ctx.measureText(name).width > c.width - 48 && size > 18)
    ctx.fillText(name, c.width / 2, c.height / 2)
    onChange(c.toDataURL('image/png'))
  }

  function switchMode(next: 'draw' | 'type') {
    if (next === mode) return
    clearCanvas()
    setTypedName('')
    setMode(next)
    onChange('')
  }

  function handleClear() {
    clearCanvas()
    setTypedName('')
    onChange('')
  }

  // The ring is not decoration: `focus:outline-none` alone left these two tabs
  // with no focus indicator whatsoever (WCAG 2.4.7). Inset, because they sit
  // inside a `p-0.5` bordered group where an outset ring would be clipped.
  const tabBase =
    'px-3 py-1 text-sm font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--color-cta-edge,#059669)]'
  const tabOn = 'bg-[var(--color-text-primary,#111827)] text-white'
  const tabOff = 'text-[var(--color-text-secondary,#4b5563)] hover:text-[var(--color-text-primary,#111827)]'

  return (
    <div>
      <div role="tablist" aria-label="Signature method" className="mb-2 inline-flex gap-0.5 rounded-md border border-[var(--color-border,#d1d5db)] p-0.5">
        <button type="button" role="tab" aria-selected={mode === 'draw'} onClick={() => switchMode('draw')} className={`${tabBase} ${mode === 'draw' ? tabOn : tabOff}`}>
          Draw
        </button>
        <button type="button" role="tab" aria-selected={mode === 'type'} onClick={() => switchMode('type')} className={`${tabBase} ${mode === 'type' ? tabOn : tabOff}`}>
          Type
        </button>
      </div>

      {mode === 'type' && (
        <input
          type="text"
          value={typedName}
          onChange={(e) => {
            setTypedName(e.target.value)
            renderTyped(e.target.value)
          }}
          placeholder="Type your full name"
          autoComplete="name"
          className="mb-2 block w-full rounded-md border border-[var(--color-border,#d1d5db)] bg-[var(--color-bg-primary,#ffffff)] px-3 py-2 text-base shadow-sm focus:border-[var(--color-primary,#000)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary,#000)]"
          aria-label="Type your signature"
        />
      )}

      <div className="relative">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={handleEnd}
          onPointerCancel={handleEnd}
          className={`w-full rounded-md border border-[var(--color-border,#d1d5db)] bg-[var(--color-bg-primary,#ffffff)] ${
            mode === 'draw' ? 'cursor-crosshair touch-none' : 'pointer-events-none'
          }`}
          aria-label={mode === 'draw' ? 'Signature pad — draw your signature' : 'Typed signature preview'}
          {...invalidProps}
        />
        <SignaturePlaceholder showHint={mode === 'draw' ? !hasInk : typedName.trim() === ''} />
      </div>

      <div className="mt-1 flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-muted,#6b7280)]">
          {mode === 'draw'
            ? 'Sign with your finger or mouse.'
            : 'Your typed name becomes your signature.'}
        </span>
        <button
          type="button"
          onClick={handleClear}
          className="text-xs text-[var(--color-text-muted,#6b7280)] underline hover:text-[var(--color-text-secondary,#4b5563)]"
        >
          Clear
        </button>
      </div>
    </div>
  )
}
